import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { LoginPromptProvider, useLoginPrompt } from "@/lib/loginPrompt";
import LoginModal from "@/components/LoginModal";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import Upload from "@/pages/Upload";
import Profile from "@/pages/Profile";
import EditProfile from "@/pages/EditProfile";
import Plans from "@/pages/Plans";
import Notifications from "@/pages/Notifications";
import Referral from "@/pages/Referral";
import Admin from "@/pages/Admin";
import Verification from "@/pages/Verification";
import VideoDetail from "@/pages/VideoDetail";
import AppLayout from "@/components/AppLayout";
import Onboarding from "@/pages/Onboarding";
import AuthPopupCallback from "@/pages/AuthPopupCallback";
import StoryCardPage from "@/pages/StoryCardPage";
import ReferralCardPage from "@/pages/ReferralCardPage";
import Analytics from "@/pages/Analytics";
import PublicFeed from "@/pages/PublicFeed";
import Rewards from "@/pages/Rewards";
import Settings from "@/pages/Settings";
import DiscoverCharacter from "@/pages/DiscoverCharacter";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import FAQ from "@/pages/FAQ";
import CastTalent from "@/pages/CastTalent";
import Advertise from "@/pages/Advertise";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AudioProvider } from "@/lib/audioContext";
import "@/App.css";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing/>}/>
      <Route path="/login" element={<Login/>}/>
      <Route path="/signup" element={<SignUp/>}/>
      <Route path="/privacy" element={<Privacy/>}/>
      <Route path="/terms" element={<Terms/>}/>
      <Route path="/faq" element={<FAQ/>}/>
      <Route path="/cast-talent" element={<CastTalent/>}/>
      <Route path="/advertise" element={<Advertise/>}/>
      <Route path="/contact" element={<Contact/>}/>
      <Route path="/careers" element={<Careers/>}/>
      <Route path="/auth-popup" element={<AuthPopupCallback/>}/>
      <Route path="/feed" element={<PublicFeed/>}/>
      {/* Public browsing — guests can view the cinematic feed without login. */}
      <Route path="/discover" element={<PublicFeed/>}/>
      <Route path="/app" element={<AppLayout/>}>
        <Route index element={<Home/>}/>
        <Route path="discover" element={<Discover/>}/>
        <Route path="upload" element={<Upload/>}/>
        <Route path="notifications" element={<Notifications/>}/>
        <Route path="me" element={<Profile/>}/>
        <Route path="edit-profile" element={<EditProfile/>}/>
        <Route path="plans" element={<Plans/>}/>
        <Route path="referral" element={<Referral/>}/>
        <Route path="verification" element={<Verification/>}/>
        <Route path="story-card" element={<StoryCardPage/>}/>
        <Route path="referral-card" element={<ReferralCardPage/>}/>
        <Route path="analytics" element={<Analytics/>}/>
        <Route path="rewards" element={<Rewards/>}/>
        <Route path="settings" element={<Settings/>}/>
        <Route path="discover-character/:character" element={<DiscoverCharacter/>}/>
        <Route path="admin" element={<Admin/>}/>
        <Route path="onboarding" element={<Onboarding/>}/>
      </Route>
      <Route path="/u/:username" element={<AppLayout/>}>
        <Route index element={<Profile/>}/>
      </Route>
      <Route path="/v/:id" element={<AppLayout/>}>
        <Route index element={<VideoDetail/>}/>
      </Route>
      <Route path="/:username" element={<Profile/>}/>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
  );
}

/** Renders the singleton Login modal driven by useLoginPrompt(). */
function GlobalLoginModal() {
  const { open, closeModal } = useLoginPrompt();
  return <LoginModal open={open} onClose={closeModal} />;
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AudioProvider>
          <AuthProvider>
            <BrowserRouter>
              <LoginPromptProvider>
                <AppRouter/>
                <GlobalLoginModal/>
                <Toaster theme="dark" position="top-center" richColors closeButton toastOptions={{ style: { background: "#180D0D", color: "#fff", border: "1px solid #27272A" } }}/>
              </LoginPromptProvider>
            </BrowserRouter>
          </AuthProvider>
        </AudioProvider>
      </ErrorBoundary>
    </div>
  );
}
